import { Anchor, Group, Stack, Text, TextInput } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import {
  EmailTakenError,
  useUpdateContact,
  type Contact,
} from '@/models/contacts';
import { FormSaveBar } from '@/components/FormSaveBar';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { useCardForm } from '@/lib/useCardForm';

type Values = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  whatsapp: string;
  telegram: string;
};

export function IdentityCard({
  contact,
  focusEmailRequest = 0,
}: {
  contact: Contact;
  focusEmailRequest?: number;
}) {
  const update = useUpdateContact();
  const [open, setOpen] = useState(true);
  const emailRef = useRef<HTMLInputElement>(null);
  const { form, dirty, saving, handleSubmit } = useCardForm<Values>({
    initialValues: {
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      whatsapp: contact.whatsapp ?? '',
      telegram: contact.telegram ?? '',
    },
    save: (dirtyValues) =>
      update.mutateAsync({ id: contact.id, ...dirtyValues }),
    successMessage: 'Identity saved',
    onError: (err, f) => {
      if (!(err instanceof EmailTakenError)) return false;
      f.setFieldError(
        'email',
        <Text span fz="xs">
          This email belongs to contact{' '}
          <Anchor component={Link} to={`~/contacts/${err.contactId}`} fz="xs">
            {err.contactId}
            {err.holderName ? ` ${err.holderName}` : ''}
          </Anchor>{' '}
          - sell on that contact
        </Text>,
      );
      return true;
    },
  });

  useEffect(() => {
    if (!focusEmailRequest) return;
    setOpen(true);
    const timer = setTimeout(() => {
      emailRef.current?.scrollIntoView({ block: 'center' });
      emailRef.current?.focus();
    }, 250);
    return () => clearTimeout(timer);
  }, [focusEmailRequest]);

  return (
    <CollapsibleCard title="Identity info" opened={open} onOpenChange={setOpen}>
      <form onSubmit={handleSubmit}>
        <Stack gap="xs">
          <Group grow gap="xs">
            <TextInput
              label="First name"
              size="xs"
              {...form.getInputProps('firstName')}
            />
            <TextInput
              label="Last name"
              size="xs"
              {...form.getInputProps('lastName')}
            />
          </Group>
          <Group grow gap="xs" align="flex-start">
            <TextInput
              ref={emailRef}
              label="Email"
              size="xs"
              {...form.getInputProps('email')}
            />
            <TextInput
              label="Phone"
              size="xs"
              {...form.getInputProps('phone')}
            />
          </Group>
          <Group grow gap="xs">
            <TextInput
              label="WhatsApp"
              size="xs"
              {...form.getInputProps('whatsapp')}
            />
            <TextInput
              label="Telegram"
              size="xs"
              {...form.getInputProps('telegram')}
            />
          </Group>
          {dirty && <FormSaveBar saving={saving} onDiscard={form.reset} />}
        </Stack>
      </form>
    </CollapsibleCard>
  );
}
